/**
 * React hooks of the preview package. The panel, the banner and the Theater / test play overlays read everything
 * through `services` (the overlays and the banner render outside the DocStoreProvider): the editor store, the user
 * settings and their `ext` preferences, the open project's store and engine, the transport and this package's own
 * stores (stage state, test play).
 */

import { useCallback, useSyncExternalStore } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { UserSettings } from '../../app/settings/types';
import type { Json, Unsubscribe } from '../../core/util/types';
import { useExternalSelector } from '../../state/react';
import { stageStateOf, type StageSnapshot } from './stageState';
import { testPlayOf, type TestPlaySnapshot } from './testPlayState';
export { useEditorValue } from '../../app/registry/react';

/** An `ext` preference through a parser (re-renders when the parsed value changes; `parse` must be stable). */
export function useExt<T>(
  services: AppServices,
  key: string,
  parse: (j: Json | undefined) => T,
  equal?: (a: T, b: T) => boolean,
): T {
  const settings = services.settings;
  const subscribe = useCallback(
    (cb: () => void): Unsubscribe =>
      settings.subscribe(() => {
        cb();
      }),
    [settings],
  );
  const get = useCallback((): UserSettings => settings.value, [settings]);
  const select = useCallback((s: UserSettings): T => parse(s.ext[key]), [key, parse]);
  return useExternalSelector(settings, subscribe, get, select, equal);
}

/** The open project's store (null in the Library); follows project switches. */
export function useProjectOpen(services: AppServices): boolean {
  const subscribe = useCallback((cb: () => void): Unsubscribe => services.onChange(cb), [services]);
  const get = useCallback((): boolean => services.store !== null && services.engine !== null, [services]);
  return useSyncExternalStore(subscribe, get, get);
}

/** Session state of the preview stage (view, pick mode, context target, popover). */
export function useStageState(services: AppServices): StageSnapshot {
  const st = stageStateOf(services);
  const subscribe = useCallback((cb: () => void): Unsubscribe => st.subscribe(cb), [st]);
  return useSyncExternalStore(subscribe, st.getSnapshot, st.getSnapshot);
}

/** The test play snapshot. */
export function useTestPlay(services: AppServices): TestPlaySnapshot {
  const tp = testPlayOf(services);
  const subscribe = useCallback((cb: () => void): Unsubscribe => tp.subscribe(cb), [tp]);
  return useSyncExternalStore(subscribe, tp.getSnapshot, tp.getSnapshot);
}

/** Runs a command through the registry as a button (toolbar, menus of this package). */
export function runCommand(services: AppServices, id: string, args?: Json): void {
  void services.registry.runAny(id, args, 'button');
}

/**
 * A value derived from the open project's document (re-evaluated on every document change and project swap). The
 * selector must return a primitive or a stable reference.
 */
export function useStoreValue<T>(services: AppServices, selector: (s: AppServices) => T): T {
  const subscribe = useCallback(
    (cb: () => void): Unsubscribe => {
      let offStore = services.store?.subscribe(() => {
        cb();
      });
      const offChange = services.onChange(() => {
        offStore?.();
        offStore = services.store?.subscribe(() => {
          cb();
        });
        cb();
      });
      return () => {
        offStore?.();
        offChange();
      };
    },
    [services],
  );
  const get = useCallback((): T => selector(services), [services, selector]);
  return useSyncExternalStore(subscribe, get, get);
}
